import { useState } from 'react'
import { useMutation } from 'react-query'
import { useHistory } from 'react-router-dom'
import { API_ERROR_CODES } from '@customTypes/Generic'
import routes from 'src/config/routes'
import { getErrorCodeFromApiError, getErrorTranslationKey } from 'src/helpers/getApiErrorTranslationKey'
import { createAppointmentForExistingPatient } from 'src/services/api/booking'
import { getAfterBookingStateFromBookingResponse } from 'src/utils/getAfterBookingStateFromBookingResponse'
import { useAppointmentCalendar } from '@context/AppointmentCalendarContext/AppointmentCalendarContext'
import { resetSelectedBookable, useChoiceDispatch, useChoiceState } from '@context/ChoiceContext'
import { setAfterBookingData, useConfig, useConfigDispatch } from '@context/ConfigurationContext'
import { useVerifyPhoneContext } from '@context/VerifyPhoneContext'
import { useCreationEvent } from './useCreationEvent'
import { useGetTreatmentInfo } from './useGetTreatmentInfo'
import { useLanguage } from './useLanguage'
import useAdditionalAppointmentHints from './useAdditionalAppointmentHints'
import { useTreatmentBundleIdForClinic } from './useTreatmentBundleIdForClinic'

export const useCreateBookingForExistingPatient = () => {
  const [error, setError] = useState('')
  const history = useHistory()
  const { selectedPatient } = useVerifyPhoneContext()
  const { clinicId, trackingParams, sourceWebsiteType } = useConfig()
  const { selectedBookableDateAndTime, verifiedPatientData } = useChoiceState()
  const treatmentBundleTypeReferenceId = useTreatmentBundleIdForClinic()
  const additionalAppointmentHints = useAdditionalAppointmentHints()
  const treatmentInfo = useGetTreatmentInfo()
  const lang = useLanguage()
  const creationEvent = useCreationEvent()
  const configDispatch = useConfigDispatch()
  const choiceDispatch = useChoiceDispatch()
  const { refresh } = useAppointmentCalendar()

  const { mutate: postBooking, isLoading } = useMutation(createAppointmentForExistingPatient, {
    onSuccess: async (result) => {
      configDispatch(setAfterBookingData(getAfterBookingStateFromBookingResponse(result?.data)))

      history.push(routes.success)
    },
    onError: (error: unknown) => {
      const errorCode = getErrorCodeFromApiError(error)
      if (errorCode === API_ERROR_CODES.BOOKING_SLOT_GONE) {
        refresh()
        choiceDispatch(resetSelectedBookable())
      }
      setError(getErrorTranslationKey(error))
    },
    retry: 0,
  })

  const createBookingForExistingPatient = () => {
    postBooking({
      clinicReferenceId: clinicId,
      patientReferenceId: selectedPatient?.patientReferenceId ?? verifiedPatientData.patientReferenceId!,
      start: selectedBookableDateAndTime.originalStartTimestamp,
      treatmentBundleReferenceId: treatmentBundleTypeReferenceId,
      employeeReferenceId: treatmentInfo.practitioner?.referenceId,
      additionalAppointmentHints,
      dataPrivacyAccepted: true,
      trackingParams,
      creationSource: sourceWebsiteType,
      lang,
      creationEvent,
    })
  }

  return { error, isLoading, createBookingForExistingPatient, resetCalendarError: () => setError('') }
}
